// ConfirmDialog — dark-themed, click-outside / Escape-to-cancel modal
// used as a replacement for ``window.confirm()`` (plan §P0.2 / §P1.11).
//
// Three confirmed callers today:
//   * ``ContentAwareG4Gate`` — when approving a flagged rewrite that
//     the classifier marked ``unresolved`` / ``needs_review``.
//   * ``PipelineRunner`` — when re-running a phase that has already
//     produced artifacts (P3 in particular costs ~$0.90 per
//     accidental click on the active LLM — see the phase briefing
//     card for the live model + provider).
//
// The component is intentionally light: it renders a fixed overlay and
// a centered ``alm-card`` with a title, body, and Cancel/Confirm CTAs.
// The legacy ``FlaggedApproveDialog`` in G4 stays put for now — we
// will migrate it in a follow-up so the bespoke red/amber palette
// stays untouched.

import { useEffect } from 'react'
import { AlertCircle } from 'lucide-react'

import { cn } from '@/lib/utils'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Visual tone of the confirm button + accent. */
  tone?: 'info' | 'warn' | 'danger'
  onCancel: () => void
  onConfirm: () => void
  /** Disabled state while the underlying mutation is in flight. */
  busy?: boolean
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'warn',
  onCancel,
  onConfirm,
  busy = false,
}: ConfirmDialogProps) {
  // Escape-to-close when the dialog is mounted.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, busy, onCancel])

  if (!open) return null

  const toneAccent =
    tone === 'danger'
      ? 'text-red-400'
      : tone === 'warn'
      ? 'text-amber-400'
      : 'text-indigo-400'
  const ctaPalette =
    tone === 'danger'
      ? 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 shadow-red-500/20'
      : tone === 'warn'
      ? 'bg-gradient-to-b from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-amber-500/20'
      : 'bg-gradient-to-b from-indigo-500 to-indigo-600 hover:from-indigo-400 hover:to-indigo-500 shadow-indigo-500/20'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm alm-fadein"
      onClick={() => !busy && onCancel()}
      role="presentation"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="alm-confirm-title"
        className="alm-card max-w-md w-[92%] mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <AlertCircle className={cn('h-5 w-5 shrink-0 mt-0.5', toneAccent)} />
          <div className="min-w-0">
            <div
              id="alm-confirm-title"
              className="text-base font-semibold text-slate-100"
            >
              {title}
            </div>
            {description ? (
              <div className="text-sm text-slate-300 mt-2 [&>p]:m-0 space-y-2">
                {description}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold text-white shadow-lg disabled:opacity-50',
              ctaPalette,
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
